import {
  DefaultApi,
  GetPrinterJpkDocumentDto,
} from '../autogenerated/spdApiClient';
import {
  PrinterDocumentType,
  printerService,
} from '../../printer/printerService';
import { PrinterJpkDocumentTypeEnum } from '../validation/enums';

export interface SyncOptions {
  upToJpkId?: number;
  maxDocs?: number;
  onProgress?: (synced: number, total: number) => void;
  stopOnError?: boolean;
  signal?: AbortSignal;
  includeMissing?: boolean;
}

interface SyncState {
  api: DefaultApi;
  printerUid: string;
  signal?: AbortSignal;
  stopOnError: boolean;
  onProgress?: (synced: number, total: number) => void;
  synced: number;
  total: number;
}

const syncDocumentBatch = async (
  jpkIds: number[],
  state: SyncState,
  printerUid: string,
): Promise<void> => {
  for (const jpkid of jpkIds) {
    if (state.signal?.aborted) {
      break;
    }
    try {
      const jsonString =
        await printerService.getDocumentFromProtectedMemory(jpkid);
      const binaryRaw = await printerService.getDocumentFromProtectedMemory(
        jpkid,
        true,
      );
      const jpkData = JSON.parse(jsonString.data) as object;
      let documentType = PrinterJpkDocumentTypeEnum.Inne;
      if (
        jsonString.type === PrinterDocumentType.DailyReport ||
        binaryRaw.type === PrinterDocumentType.FiscalReport
      ) {
        documentType = PrinterJpkDocumentTypeEnum.Raport;
      } else if (
        jsonString.type === PrinterDocumentType.Receipt ||
        binaryRaw.type === PrinterDocumentType.ReceiptCancelled
      ) {
        documentType = PrinterJpkDocumentTypeEnum.Paragon;
      }
      await state.api.printerJpkDocumentControllerCreate({
        jpkId: jpkid,
        printerUid,
        jpkData,
        documentType,
        binaryData: binaryRaw.data ? btoa(binaryRaw.data) : undefined,
      });
    } catch (docError) {
      console.error(`[JPK sync] Error at jpkId=${jpkid}:`, docError);
      if (state.stopOnError) {
        break;
      }
    }
    state.synced++;
    state.onProgress?.(state.synced, state.total);
  }
};

export interface PrinterJpkDocumentContextInterface {
  sync: (printerUid: string, options?: SyncOptions) => Promise<void>;
  getById: (id: string) => Promise<GetPrinterJpkDocumentDto>;
  getAllByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GetPrinterJpkDocumentDto[]>;
  getAllCount: (filterText?: string, filterColumns?: string) => Promise<number>;
}

export const PrinterJpkDocumentContext = (
  api: DefaultApi,
): PrinterJpkDocumentContextInterface => ({
  sync: async (
    printerUid: string,
    options: SyncOptions = {},
  ): Promise<void> => {
    const {
      upToJpkId,
      maxDocs,
      onProgress,
      stopOnError = false,
      signal,
      includeMissing = true,
    } = options;

    const results = await Promise.all([
      Promise.all([
        api.printerJpkDocumentControllerGetHighestJpkId(printerUid),
        includeMissing
          ? api.printerJpkDocumentControllerGetMissingJpkIds(printerUid)
          : Promise.resolve({ missingIds: [] }),
      ]),
      upToJpkId !== undefined
        ? Promise.resolve(upToJpkId)
        : printerService.getLastJpkId(),
    ]);
    const [[{ jpkId: serverHighestId }, { missingIds = [] }], endId] = results;

    const startId = (serverHighestId ?? 0) + 1;
    const cappedEndId =
      maxDocs !== undefined ? Math.min(endId, startId + maxDocs - 1) : endId;
    const newCount = cappedEndId >= startId ? cappedEndId - startId + 1 : 0;
    const total = newCount + (includeMissing ? missingIds.length : 0);

    if (newCount <= 0 && (!includeMissing || missingIds.length === 0)) {
      return;
    }

    if (total === 1) {
      // wait moment before syncing single document to ensure any ongoing printer operations are completed
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    const state: SyncState = {
      api,
      printerUid,
      signal,
      stopOnError,
      onProgress,
      synced: 0,
      total,
    };

    const newIds = Array.from({ length: newCount }, (_, i) => startId + i);
    await syncDocumentBatch(newIds, state, printerUid);
    if (includeMissing) {
      await syncDocumentBatch(missingIds, state, printerUid);
    }
  },

  getById: (id: string) => api.printerJpkDocumentControllerGetById(id),

  getAllByPortion: async (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ): Promise<GetPrinterJpkDocumentDto[]> => {
    return api.printerJpkDocumentControllerGetAllByPortion(
      pageSize,
      pageNumber,
      orderBy || 'documentTs',
      orderDirection || 'DESC',
      filterText,
      filterColumns,
    );
  },

  getAllCount: async (
    filterText?: string,
    filterColumns?: string,
  ): Promise<number> => {
    return api.printerJpkDocumentControllerGetAllCount(
      filterText,
      filterColumns,
    );
  },
});
